import {
  OrganizationService,
  SendSMSNodeData,
  SMSCommunicationPlatform,
  SMSCommunicationPlatformConfig,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Loader2 } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';
import {
  useAutoSaveDebouncedCallback,
  useNodeConfigurationEventListener,
} from '../utils/nodeConfigurationUtils';
import { TestNodeDrawer } from './TestNodeDrawer';

const googleOAuthClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
console.log('google client id', googleOAuthClientId);

export default function SendSMSConfiguration({
  selectedNode,
  setNodes,
  readOnly = false,
  setAllRequiredFieldsOfNodeConfigurationFilled,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
  setAllRequiredFieldsOfNodeConfigurationFilled: Dispatch<
    SetStateAction<boolean>
  >;
}) {
  const wF = useStore($workflow);
  const org = useStore($org);
  const nodes = useNodes();
  const queryClient = useQueryClient();
  const [testDrawerOpen, setTestDrawerOpen] = useState(false);

  console.log('org', org);

  const {
    data: orgPhoneNumbers,
    error,
    isLoading: loadingOrgPhoneNumbers,
    isFetching: fetchingOrgPhoneNumbers,
    refetch,
  } = useQuery({
    queryKey: ['orgPhoneNumbers', org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      if (!org?.id) return [];
      const res = await OrganizationService.getOrganizationPhoneNumbers(
        org?.id || '',
      );
      return res;
    },
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // Keep config as it is
  // Keep config as it is
  const [communicationPlatformConfig, setCommunicationPlatformConfig] =
    useState<SMSCommunicationPlatformConfig | {}>({
      accountSid: '',
      authToken: '',
      phoneNumber: '+18035908513',
    });
  const [smsBody, setSmsBody] = useState<string>('');
  const [toPhoneNumber, setToPhoneNumber] = useState<string>('');
  const [communicationPlatform, setCommunicationPlatform] =
    useState<SMSCommunicationPlatform | null>('TWILIO');

  function isSendSMSNodeData(data: any): data is SendSMSNodeData {
    return data.type === 'SEND_SMS';
  }

  async function saveConfigurations() {
    if (!readOnly) {
      if (!toPhoneNumber) {
        toast.error("Please enter a recipient's phone number!");
        return;
      }
      if (!smsBody) {
        toast.error('Please enter the sms body!');
        return;
      }

      $savingNodeConfiguration.set(true);
      setAllRequiredFieldsOfNodeConfigurationFilled(true);
      const updatableNode = updateNodeLocally();
      if (updatableNode) {
        await updateNode.mutateAsync({
          req: {
            data: { ...updatableNode.data },
          },
          nodeId: updatableNode?.id,
        });
      }
      $savingNodeConfiguration.set(false);
    }
  }

  useAutoSaveDebouncedCallback(
    async () => {
      await saveConfigurations();
    },
    2000, // 1 seconds delay
    [smsBody] as const,
  );

  useNodeConfigurationEventListener('confirmSaveChanges', async detail => {
    await saveConfigurations();
  });

  function updateNodeLocally() {
    if (readOnly) return;
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode: FormattedNode = newNodes.find(
      (n: any) => n.id === selectedNode.id,
    );

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      updatableNode.data = {
        ...updatableNode.data,
        type: 'SEND_SMS' as WorkflowNodeType,
      };
      if (isSendSMSNodeData(updatableNode.data)) {
        if (communicationPlatformConfig) {
          updatableNode.data = {
            ...updatableNode.data,
            communication_platform_config:
              communicationPlatformConfig as SMSCommunicationPlatformConfig,
          };
        }
        if (toPhoneNumber) {
          updatableNode.data = {
            ...updatableNode.data,
            to_phone_number: toPhoneNumber,
          };
        }
        if (smsBody) {
          updatableNode.data = {
            ...updatableNode.data,
            sms_body: smsBody,
          };
        }
        if (communicationPlatform) {
          updatableNode.data = {
            ...updatableNode.data,
            communication_platform: communicationPlatform,
          };
        }
      }
      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;
      setNodes(newNodes);
      return updatableNode;
    }
  }

  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Configurations updated!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  useEffect(() => {
    if (wF) {
      if (wF.type === 'BulkRun') {
        setToPhoneNumber('{UserPhoneNumber}');
      }
    }
    if (selectedNode) {
      if (isSendSMSNodeData(selectedNode.data)) {
        setCommunicationPlatform(
          selectedNode.data.communication_platform ?? null,
        );
        setCommunicationPlatformConfig(
          selectedNode.data.communication_platform_config ?? {},
        );
        setToPhoneNumber(selectedNode.data.to_phone_number ?? '');
        setSmsBody(selectedNode.data.sms_body ?? '');
      }
    }
  }, [selectedNode]);

  // ---------------------------------------------------------------------
  // UI / JSX
  // ---------------------------------------------------------------------
  return (
    <>
      <div className="flex h-auto w-full flex-col bg-gray-50">
        <div className="flex-1 overflow-y-auto">
          <div className="m-auto mx-auto max-w-sm bg-white p-4 shadow-sm">
            {/* Gmail Account Section (unchanged) */}
            {/* <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Communication Platform
              </label>

              <Select
                onValueChange={value => {
                  // update the data ingestion node configuration
                  setCommunicationPlatform(value as SMSCommunicationPlatform);
                }}
                value={communicationPlatform as string} // @NOTE: Need to fix the types later on
              >
                <SelectTrigger className="w-[100%]">
                  <SelectValue placeholder="Select a communication platform" />
                </SelectTrigger>
                <SelectContent className="w-[100%]">
                  <SelectGroup>
                    <SelectLabel>Sources</SelectLabel>
                    <SelectItem value="TWILIO">Twilio</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div> */}

            {/* From Field (local state) */}
            <div className="">
              <label
                htmlFor="to"
                className="flex items-center text-sm font-medium text-gray-700"
              >
                From{' '}
                {(loadingOrgPhoneNumbers || fetchingOrgPhoneNumbers) && (
                  <Loader2 className="ml-2 h-3 w-3 animate-spin" />
                )}
              </label>

              <div className="mt-1 rounded-md bg-gray-100 p-2 text-sm">
                {/* @TODO: Here we are showing the first number in the list assuming that
                there will be only one number per org for now, later on we have to improve this
                to show the number in  more robust way */}
                {orgPhoneNumbers &&
                  orgPhoneNumbers?.length > 0 &&
                  orgPhoneNumbers[0]?.phoneNumber}
              </div>
            </div>

            {/* To Field (local state) */}
            <div className="mt-4">
              <label
                htmlFor="to"
                className="block text-sm font-medium text-gray-700"
              >
                To
              </label>
              <Input
                id="to"
                name="to"
                disabled={readOnly || wF?.type === 'BulkRun'}
                value={toPhoneNumber} // local state
                onChange={e => {
                  if (!readOnly) {
                    setToPhoneNumber(e.target.value);
                  }
                }}
                className={`mt-1 font-semibold text-blue-800 ${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
                placeholder="Recipient's email address"
              />
            </div>

            {/* CC / BCC Fields (unchanged) */}

            {/* Subject (local state) */}
            <div className="relative mt-4">
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-gray-700"
              >
                SMS Body
              </label>
              <span className="absolute right-0 top-0 text-xs font-medium text-gray-500">
                Prompt AI
              </span>
              <Textarea
                id="body"
                name="body"
                value={smsBody} // local state
                onChange={e => {
                  if (!readOnly) {
                    setSmsBody(e.target.value);
                  }
                }}
                className={`mt-1 ${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
                placeholder="Write your message here..."
                rows={6}
              />
            </div>

            <p className="text-sm text-gray-500">
              Variables: [company_name], [customer_name], [invoice_name],
              [phone_number], [email], [street_address], [city], [state],
              [zip_code], [country], [invoice_amount], [invoice_paid_amount],
              [invoice_balance], [invoice_due_date], [invoice_invoice_date],
              [payment_link], [company_phone_number]
            </p>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={readOnly || updateNode.isPending} // ✅ Prevent save when read-only
                onClick={async () => {
                  setTestDrawerOpen(true);
                }}
                className={`px-3 py-1 text-sm${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
              >
                Test
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={readOnly || updateNode.isPending} // ✅ Prevent save when read-only
                      onClick={async () => {
                        await saveConfigurations();
                      }}
                      className={`px-3 py-1 text-sm${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
                    >
                      Save
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Changes are automatically saved after editing</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      <TestNodeDrawer
        open={testDrawerOpen}
        onClose={() => setTestDrawerOpen(false)}
        node={selectedNode}
        workflow={wF}
      />
    </>
  );
}
